import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getWatchlist, getReferralStats, getNotifications } from '../api';
import { VideoCard } from '../components/VideoCard';
import { User, Clock, Heart, Star, MessageCircle, Users, Loader, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const MyProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [watchlist, setWatchlist] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user) {
      fetchProfileData();
    }
  }, [user]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const [watchlistRes, referralRes, notificationsRes] = await Promise.all([
        getWatchlist(),
        getReferralStats(),
        getNotifications(false, 5, 0)
      ]);

      setWatchlist(watchlistRes.data.videos || []);
      setReferralStats(referralRes.data);
      setNotifications(notificationsRes.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Veuillez vous connecter</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="container max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/20 to-accent rounded-lg p-8 mb-8">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {user.username}
              </h1>
              <p className="text-muted-foreground mb-3">{user.email}</p>
              <div className="flex items-center gap-4">
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium">
                  {user.role === 'creator' ? '🎬 Créateur' : user.role === 'superadmin' ? '👑 Admin' : '👤 Viewer'}
                </span>
                {referralStats && (
                  <span className="px-3 py-1 bg-accent text-foreground rounded-full text-sm font-medium">
                    ⭐ {referralStats.current_points} points
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate('/settings')}
              className="px-4 py-2 bg-card border border-border rounded-lg hover:bg-accent transition-colors"
            >
              Paramètres
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'overview'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            Vue d'ensemble
          </button>
          <button
            onClick={() => setActiveTab('watchlist')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'watchlist'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            Ma liste ({watchlist.length})
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'notifications'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            Notifications
          </button>
          {user.role !== 'viewer' && (
            <button
              onClick={() => navigate('/creator-studio')}
              className="px-4 py-2 bg-accent text-foreground rounded-lg font-medium hover:bg-accent/80 transition-colors"
            >
              📊 Mon Studio
            </button>
          )}
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Heart className="w-5 h-5 text-primary" />
                  <h3 className="font-medium text-foreground">Ma Liste</h3>
                </div>
                <p className="text-3xl font-bold text-foreground">{watchlist.length}</p>
                <p className="text-sm text-muted-foreground mt-1">vidéos sauvegardées</p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-medium text-foreground">Parrainages</h3>
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {referralStats?.total_referrals || 0}
                </p>
                <p className="text-sm text-muted-foreground mt-1">personnes parrainées</p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Star className="w-5 h-5 text-primary" />
                  <h3 className="font-medium text-foreground">Points</h3>
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {referralStats?.current_points || 0}
                </p>
                <p className="text-sm text-muted-foreground mt-1">points accumulés</p>
              </div>
            </div>

            {/* Code de parrainage */}
            {referralStats && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-medium text-foreground mb-4">Mon Code de Parrainage</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1 bg-accent px-4 py-3 rounded-lg">
                    <p className="text-2xl font-mono font-bold text-primary">
                      {referralStats.referral_code}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(referralStats.referral_code);
                      alert('Code copié!');
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    Copier
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Partagez ce code pour gagner 10 points par parrainage!
                </p>
              </div>
            )}

            {/* Recent notifications */}
            {notifications.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-medium text-foreground mb-4">Notifications Récentes</h3>
                <div className="space-y-3">
                  {notifications.slice(0, 3).map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 bg-accent rounded-lg"
                    >
                      <Bell className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-foreground">{notif.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{notif.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'watchlist' && (
          <div>
            {watchlist.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Votre liste est vide</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {watchlist.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div>
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Aucune notification</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-4 rounded-lg border ${
                      notif.read_at ? 'bg-card border-border' : 'bg-accent border-primary/20'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-foreground mb-1">{notif.title}</p>
                        <p className="text-sm text-muted-foreground">{notif.body}</p>
                      </div>
                      {!notif.read_at && (
                        <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
